// Renders its children only once the placeholder scrolls near the viewport (IntersectionObserver), so long grids fetch images lazily.
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/cn";

interface LazyVisibleProps {
  children: ReactNode;
  placeholder: ReactNode;
  rootMargin?: string;
  className?: string;
}

export function LazyVisible({ children, placeholder, rootMargin = "240px", className }: LazyVisibleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const element = ref.current;
    if (visible || element === null) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [visible, rootMargin]);
  return (
    <div ref={ref} className={cn("min-w-0", className)}>
      {visible ? children : placeholder}
    </div>
  );
}
